/**
 * Receiver download loop — the pure core of the offscreen transfer host's
 * per-part and whole-transfer receive pipeline (docs/file-transfer-resume.md
 * §6.2 step 7; TASK-17 brief item 2). Fully unit-testable via fakes (see
 * `__tests__/receiveLoop.test.ts`) — no `browser.*`/OPFS/network here.
 *
 * MULTI-FILE (docs/superpowers/specs/2026-07-26-multi-file-transfer-design.md
 * §4/§6): this loop is now PLAN-DRIVEN — every global part is resolved via a
 * `partPlan` entry to exactly one file's file-LOCAL chunk range (a part never
 * straddles a file boundary), and every AAD/write/hash operation uses that
 * file's own id and LOCAL chunk index. The one deliberate exception is the AEAD
 * NONCE counter, which is the file-aligned GLOBAL chunk index (`globalChunkOffsets`,
 * crypto-spec.md §6a): a transfer has one key and one `noncePrefix`, so a per-file
 * counter would repeat a (key, nonce) pair across files. A single-file transfer is
 * the degenerate one-file plan (`fileIndex` always 0, global === local), so this is
 * byte-identical to the pre-multi-file loop for every existing caller.
 *
 * Buffer discipline (TASK-17 brief's "Controller resolutions"): at most one
 * ciphertext chunk is buffered by the splitter at a time, and one PART's
 * worth of decrypted plaintext (bounded, `CHUNKS_PER_PART * chunkSize` ≈
 * 5 MiB) is held only long enough to feed the running per-file hash AFTER
 * the part fully authenticates — see the module-doc note on hashing below.
 * Nothing here ever buffers a whole file, however large.
 *
 * WRITE vs HASH granularity (docs/file-transfer-resume.md §6.2 step 7 bullet
 * — normative, read before changing either loop):
 *   - WRITE: each chunk is written to `outputFor(fileIndex).writeAt(localChunkIndex
 *     * chunkSize, plaintext)` immediately as it decrypts (the spec's pinned
 *     extension granularity) — offsets are deterministic, so a same-part
 *     retry's rewrite of an already-written prefix is a harmless identical
 *     overwrite.
 *   - HASH: the running per-file SHA-256 is fed only ONCE the part's chunks
 *     have ALL authenticated (i.e. after the loop below, not inside it).
 *     Feeding the hash per-chunk-as-decrypted would double-count a part's
 *     already-hashed prefix on the re-fetch-once retry path (a streaming
 *     digest cannot be "un-fed") and silently corrupt the per-file
 *     verification. Buffering one part's plaintext just for this purpose is
 *     the documented, bounded exception to "no whole-file buffering" (M2
 *     memory-flatness acceptance criterion). Controller-accepted deviation
 *     from the brief's literal "one chunk plaintext" phrasing (TASK-17
 *     brief's "Controller resolutions"): the accepted bound is one PART's
 *     plaintext (~5 MiB), not one chunk — there is no hash-clone/rewind
 *     primitive in `@reflecto/core` to make a one-chunk bound possible without
 *     re-reading already-written bytes back from OPFS on every retry, and
 *     Android's M1 `Receiver.kt` already holds a whole part's `ByteArray`s in
 *     memory (part-atomic write) — so this extension mirrors that same
 *     established, reviewed bound rather than inventing a stricter one.
 *
 * PER-FILE VERIFICATION (design doc §6): `runReceiveLoop` finalizes and
 * compares a file's digest the moment THIS CALL's loop writes that file's own
 * last chunk (`entry.localChunkEndInclusive === file.chunkCount - 1`) — not
 * only once at the very end of the whole transfer. This lets a later file's
 * mismatch (or an earlier one's) surface as soon as it's detectable, and is
 * required for multi-file: `receiveHost.ts`'s single call to this loop covers
 * every file in one pass, so verification MUST happen per-file, inline. A
 * mismatch throws `HashMismatchError`, aborting the whole (atomic) transfer.
 * CAVEAT for a RESUMED loop (`startPartIndex > 0`): a file whose last chunk
 * was already written by an EARLIER call (before this invocation's
 * `startPartIndex`) is never re-touched here, so this loop never verifies it
 * — a streaming digest can only be finalized once, so re-verifying here would
 * require a second `hash.final` call on an already-finalized state. The one
 * resumed caller, `receiveResume.ts`, owns exactly this per-file "already
 * done pre-crash, skip" bookkeeping (slice 1c — see its module doc's
 * "MULTI-FILE RESUME" section for the full derivation), for every file in
 * the transfer, not just a single degenerate one.
 *
 * M1-mirrored chunk-auth semantics (Android `Receiver.kt`, kept verbatim):
 * an AEAD failure on any chunk discards the part's remaining work and
 * re-fetches the WHOLE part once (fresh presigned URL via `fetchPart`); a
 * second failure propagates as `ChunkAuthError` for the caller (receiveHost.ts)
 * to turn into the terminal `CHUNK_AUTH_FAIL` path.
 */

import { ChunkAuthError } from "@reflecto/core";
import { partChunkCipherLengths, splitCiphertextStream } from "./ciphertextSplitter";
import { globalChunkOffsets, type PartPlanEntry } from "./partPlan";

export interface ReceiveLoopCrypto {
  decryptChunk(
    key: Uint8Array,
    noncePrefix: Uint8Array,
    ids: { transferId: string; fileId: string },
    /** File-LOCAL chunk index — the AAD's `chunkIndex` (crypto-spec.md §6). */
    chunkIndex: number,
    isFinal: boolean,
    ciphertext: Uint8Array,
    /**
     * File-aligned GLOBAL chunk index — the NONCE counter (crypto-spec.md §6a), which
     * must match what the sender used or the tag fails to verify. Required here, unlike
     * on the `@reflecto/core` primitive (where it defaults to `chunkIndex` for the
     * single-file callers), because the engine is multi-file.
     */
    nonceChunkIndex: number
  ): Promise<Uint8Array>;
}

export interface ReceiveLoopHash {
  update(state: unknown, chunk: Uint8Array): void;
  /** Finalize a streaming hash state — destructive/one-shot (mirrors libsodium's `crypto_hash_sha256_final`): calling this twice on the SAME state is unsafe, so every caller (this loop, `receiveResume.ts`'s degenerate fallback) must guarantee exactly one `final` per hash state. */
  final(state: unknown): Uint8Array;
}

/** The writer-worker seam (TASK-17 brief: "post plaintext to the writer worker"). `flush` durably lands a part's writes before P-R2 (docs/file-transfer-resume.md §4.2). */
export interface ReceiveLoopOutput {
  writeAt(offset: number, data: Uint8Array): Promise<void>;
  flush(): Promise<void>;
}

/** One part's downloaded ciphertext, as a stream — never the whole response buffered eagerly. */
export interface FetchedPart {
  stream: ReadableStream<Uint8Array>;
}

export class HashMismatchError extends Error {
  constructor() {
    super("whole-file hash verification failed");
    this.name = "HashMismatchError";
  }
}

function bytesToHex(bytes: Uint8Array): string {
  let hex = "";
  for (const b of bytes) hex += b.toString(16).padStart(2, "0");
  return hex;
}

export interface ReceivePartParams {
  /** Fetch (a fresh presigned URL for) one GLOBAL part's ciphertext stream. Called once per attempt — the retry-once policy calls it again on a `ChunkAuthError`. */
  fetchPart(partIndex: number): Promise<FetchedPart>;
  crypto: ReceiveLoopCrypto;
  hash: ReceiveLoopHash;
  /**
   * File-aligned global-part -> per-file chunk-range map (multi-file transfer design §4,
   * docs/superpowers/specs/2026-07-26-multi-file-transfer-design.md). Indexed by the GLOBAL
   * part index (`plan[partIndex].globalPart === partIndex`) — a single-file transfer's plan
   * is the degenerate one-file case (`fileIndex` always 0), so this loop is byte-identical
   * to the pre-multi-file per-file math.
   */
  plan: readonly PartPlanEntry[];
  /** Each file's manifest metadata needed for length/isFinal/verify math, indexed by `fileIndex` (same indexing as `plan`). */
  files: readonly {
    readonly fileId: string;
    readonly chunkCount: number;
    readonly size: number;
    readonly sha256: string;
  }[];
  /** Resolve file `fileIndex`'s output handle — never the same handle across two different files. */
  outputFor(fileIndex: number): ReceiveLoopOutput;
  /** Resolve file `fileIndex`'s PERSISTENT streaming hash state — one `hash.init()` per file, pre-created by the caller before the loop starts. */
  hashStateFor(fileIndex: number): unknown;
  transferKey: Uint8Array;
  noncePrefix: Uint8Array;
  transferId: string;
  /** Plaintext bytes per (non-final) chunk — uniform across every file in the transfer (`manifest.chunkSize`). */
  chunkSize: number;
}

/** Downloads + decrypts + writes ONE global part's chunks, in ascending file-LOCAL chunk-index order. No retry — see `receivePartWithRetry`. */
async function receivePartOnce(
  partIndex: number,
  params: ReceivePartParams,
  chunkOffsets: readonly number[]
): Promise<void> {
  const entry = params.plan[partIndex];
  const file = params.files[entry.fileIndex];
  const output = params.outputFor(entry.fileIndex);
  const lengths = partChunkCipherLengths(
    entry.localChunkStart,
    entry.localChunkEndInclusive,
    file.chunkCount,
    file.size,
    params.chunkSize
  );

  const { stream } = await params.fetchPart(partIndex);

  // Bounded to ONE part's plaintext (see module doc's HASH-vs-WRITE note) —
  // released once this function returns; never accumulates across parts.
  const partPlaintexts: Uint8Array[] = [];
  let localChunk = entry.localChunkStart;
  for await (const ciphertext of splitCiphertextStream(stream, lengths)) {
    // The AAD index is file-LOCAL and the NONCE counter is the file-aligned GLOBAL
    // index — the sender's exact split (see uploadLoop.ts's `encryptPart` and
    // crypto-spec.md §6/§6a). Both are derived from the manifest, never from the wire.
    const isFinal = localChunk === file.chunkCount - 1;
    const plaintext = await params.crypto.decryptChunk(
      params.transferKey,
      params.noncePrefix,
      { transferId: params.transferId, fileId: entry.fileId },
      localChunk,
      isFinal,
      ciphertext,
      chunkOffsets[entry.fileIndex] + localChunk
    );
    await output.writeAt(localChunk * params.chunkSize, plaintext);
    partPlaintexts.push(plaintext);
    localChunk++;
  }
  await output.flush();

  // Hash only after the WHOLE part authenticated — see module doc. Every chunk in a
  // part belongs to the SAME file (partPlan is file-aligned), so one hash state suffices.
  const hashState = params.hashStateFor(entry.fileIndex);
  for (const plaintext of partPlaintexts) {
    params.hash.update(hashState, plaintext);
  }
}

/**
 * M1 `Receiver.kt`-mirrored semantics: on `ChunkAuthError`, discard the
 * part's work and re-fetch the WHOLE part once (a fresh `fetchPart` call —
 * fresh presigned URL); a second failure propagates.
 */
export async function receivePartWithRetry(
  partIndex: number,
  params: ReceivePartParams,
  /**
   * Per-file GLOBAL chunk offsets (the nonce counter's per-file base, crypto-spec.md
   * §6a). `runReceiveLoop` computes them once and threads them through; the default
   * derives the SAME array from `params.files` for a standalone single-part call.
   */
  chunkOffsets: readonly number[] = globalChunkOffsets(params.files.map((f) => f.chunkCount))
): Promise<void> {
  try {
    await receivePartOnce(partIndex, params, chunkOffsets);
  } catch (err) {
    if (!(err instanceof ChunkAuthError)) throw err;
    await receivePartOnce(partIndex, params, chunkOffsets); // one retry — a second failure propagates
  }
}

export interface ReceiveLoopParams extends ReceivePartParams {
  partCount: number;
  /** TASK-15 resume seam: where to start the download loop (default 0 — a fresh M2 receive always starts at part 0; resume computes `ceil(resumeChunk / CHUNKS_PER_PART)`, §6.2 step 7). */
  startPartIndex?: number;
  /**
   * Persist-after-part hook (docs/file-transfer-resume.md P-R2 seam) — called after a
   * part's chunks are ALL decrypted, written, AND flushed (and, if this part completed
   * a file, that file's hash verified), with the high-water `chunksVerified` cursor.
   * MULTI-FILE: `chunksVerified` is the GLOBAL cursor across every file in the transfer
   * (cumulative chunkCount of every earlier file + this file's own local high-water
   * chunk index + 1) — a whole-transfer progress cursor, in the same spirit as
   * `sendFile.ts`'s aggregate PASS-1 hashing progress. For a single-file plan
   * (`fileIndex` always 0) this is byte-identical to the pre-multi-file per-file
   * cursor, so TASK-15's `FtReceiverStateRecord` persistence (single-file only this
   * slice) is unaffected. See this module's doc for the decision.
   */
  onPartWritten?(partIndex: number, chunksVerified: number): Promise<void>;
  /**
   * TASK-19 sibling hook, fired right after `onPartWritten` resolves, with
   * the same high-water `chunksVerified` cursor — the receiver's throttled
   * WIRE `progress` ctrl seam (`progressThrottle.ts`, §8.2/§9.2's "progress
   * ctrl (throttled)" row). Callers bind a `createThrottledProgressSender`
   * instance here; local (own-side) UI progress stays on `onProgress`
   * below, which is NEVER throttled.
   */
  onProgressCtrl?(chunksVerified: number): void;
  onProgress?(partIndex: number, partCount: number): void;
  /** Cooperative cancel/unpair check, polled between parts (not sub-part — see receiveHost.ts's module doc for why that granularity is sufficient here). */
  isAborted?(): boolean;
}

export class ReceiveAbortedError extends Error {
  constructor() {
    super("receive loop aborted");
    this.name = "ReceiveAbortedError";
  }
}

/**
 * The ordered decrypt+write(+verify) pipeline across every GLOBAL part from
 * `startPartIndex` to `partCount - 1` — the "one code path" TASK-15's resume
 * loop reuses verbatim (mirrors `uploadLoop.ts`'s role for the sender).
 */
export async function runReceiveLoop(params: ReceiveLoopParams): Promise<void> {
  const startPartIndex = params.startPartIndex ?? 0;

  // Precompute each file's GLOBAL chunk offset (cumulative chunkCount of every
  // earlier file) once — feeds BOTH the per-chunk nonce counter (crypto-spec.md §6a)
  // and `onPartWritten`'s `chunksVerified` cursor below. Always 0 for fileIndex 0, so
  // a single-file plan is unaffected.
  const fileChunkOffsets = globalChunkOffsets(params.files.map((f) => f.chunkCount));

  for (let partIndex = startPartIndex; partIndex < params.partCount; partIndex++) {
    if (params.isAborted?.()) throw new ReceiveAbortedError();
    params.onProgress?.(partIndex, params.partCount);
    await receivePartWithRetry(partIndex, params, fileChunkOffsets);

    const entry = params.plan[partIndex];
    const file = params.files[entry.fileIndex];

    // Per-file verification the moment THIS call's loop writes the file's own
    // last chunk — see module doc's "PER-FILE VERIFICATION" note (and its
    // resumed-loop caveat).
    if (entry.localChunkEndInclusive === file.chunkCount - 1) {
      const digestHex = bytesToHex(params.hash.final(params.hashStateFor(entry.fileIndex)));
      if (digestHex.toLowerCase() !== file.sha256.toLowerCase()) {
        throw new HashMismatchError();
      }
    }

    const chunksVerified = fileChunkOffsets[entry.fileIndex] + entry.localChunkEndInclusive + 1;
    await params.onPartWritten?.(partIndex, chunksVerified);
    params.onProgressCtrl?.(chunksVerified);
  }
}

/**
 * Whether the save handoff is CONFIRMED finished, which decides if the core
 * may delete the staged OPFS copy.
 *
 * - `"durable"` — the file is fully written where the user wants it (the
 *   extension awaits Chrome's COMPLETE, web's `showSaveFilePicker` awaits
 *   `writable.close()`, the CLI has renamed the temp file). Safe to delete.
 * - `"detached"` — the handoff was started but cannot be awaited. Web's
 *   anchor-download fallback (Firefox/Safari/older Chromium) is the case:
 *   `a.click()` returns immediately while the browser reads the blob in the
 *   background. Deleting the copy here corrupts that read — measured in
 *   Chromium, a `File` (AND a `new File([...])` re-wrap of it) backed by an
 *   OPFS entry throws `NotFoundError` the moment the entry is removed; the
 *   wrap does NOT snapshot the bytes. Cleanup falls to `gcOrphanTransfers`,
 *   which sweeps any `ft/` file whose transferId has no ACTIVE record — the
 *   record is terminal by then, so the copy is reclaimed on the next load.
 */
export type ReceiveHandoffDisposition = "durable" | "detached";
